<template>
    <div class="event-basics">
        <div v-if="missing.length" class="event-basics__missing">
            {{ langJson('Still needed to complete Basics') }}: {{ missing.map((label) => langJson(label)).join(', ') }}
        </div>
        <div class="event-basics__card">
            <h3 class="event-basics__card-title">{{ langJson('Event title') }}</h3>
            <div :class="fieldClass('title')">
                <FormItem v-model="form.title" type="text" :matchs="['unull']" :disabled="locked('title')" :title="langJson('Event Name')"></FormItem>
            </div>
            <div class="event-basics__row">
                <div :class="[fieldClass('eventDate'), 'event-basics__field']">
                    <div class="event-basics__label">{{ langJson('Event Date(s)') }} <span class="event-basics__req">*</span></div>
                    <el-date-picker
                        v-model="form.eventDates"
                        class="event-basics__picker"
                        type="datetimerange"
                        format="dd/MM/yy HH:mm"
                        :default-time="['10:00:00', '22:00:00']"
                        :disabled="locked('eventDate')"
                        :start-placeholder="langJson('Start')"
                        :end-placeholder="langJson('End')"
                        range-separator="–"
                    ></el-date-picker>
                </div>
                <div :class="[fieldClass('timezone'), 'event-basics__field']">
                    <div class="event-basics__label">{{ langJson('Timezone') }} <span class="event-basics__req">*</span></div>
                    <el-select v-model="form.timezone" class="event-basics__picker" filterable :disabled="locked('timezone')">
                        <el-option v-for="zone in timezones" :key="zone" :label="zone.replace(/_/g, ' ')" :value="zone"></el-option>
                    </el-select>
                </div>
            </div>
            <div :class="fieldClass('venue')">
                <FormItem v-model="form.venue" type="text" :matchs="['unull']" :disabled="locked('venue')" :title="langJson('Event Location')" :placeholder="langJson('e.g. Siloso Beach, Singapore')"></FormItem>
            </div>
            <div class="event-basics__row">
                <div :class="[fieldClass('websiteUrl'), 'event-basics__field']">
                    <FormItem v-model="form.websiteUrl" type="text" :disabled="locked('websiteUrl')" :title="langJson('Website')" placeholder="https://"></FormItem>
                </div>
                <div :class="[fieldClass('termsUrl'), 'event-basics__field']">
                    <FormItem v-model="form.termsUrl" type="text" :disabled="locked('termsUrl')" :title="langJson('Terms & Conditions')" placeholder="https://"></FormItem>
                </div>
            </div>
            <div :class="[fieldClass('saleStart'), 'event-basics__field']">
                <div class="event-basics__label">{{ langJson('Ticket sales window') }} <span class="event-basics__optional">{{ langJson('(optional — defaults to launch until the event starts)') }}</span></div>
                <el-date-picker
                    v-model="form.saleDates"
                    class="event-basics__picker"
                    type="datetimerange"
                    format="dd/MM/yy HH:mm"
                    :disabled="locked('saleStart')"
                    :start-placeholder="langJson('Sales open')"
                    :end-placeholder="langJson('Sales close')"
                    range-separator="–"
                ></el-date-picker>
            </div>
        </div>

        <div :class="['event-basics__card', fieldClass('ticketImageUrl')]">
            <FormItem
                v-model="form.ticketImageUrl"
                type="image"
                :title="langJson('Event Banner')"
                :uploadMsg="langJson('Recommended ratio: 16 × 9')"
                :uploadTip="langJson('(Acceptable file format: JPEG, JPG, PNG.)')"
            ></FormItem>
        </div>

        <div class="event-basics__card">
            <h3 class="event-basics__card-title">{{ langJson('Event Description') }}</h3>
            <div class="event-basics__desc">
                <div :class="fieldClass('descriptionImageUrl')">
                    <FormItem
                        v-model="form.descriptionImageUrl"
                        type="image"
                        :uploadMsg="langJson('Recommended ratio: 4 × 3')"
                        :uploadTip="langJson('(Acceptable file format: JPEG, JPG, PNG.)')"
                    ></FormItem>
                </div>
                <div :class="fieldClass('description')">
                    <FormItem v-model="form.description" type="textarea" height="220px" :matchs="['unull']" :disabled="locked('description')" :title="langJson('Write about your event')"></FormItem>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import organizerApi from '@/api/organizer'
import { apiErrorMessage, browserTimezone, timezoneOptions, utcToWallClock, wallClockToUtc } from '@/assets/js/organizerEvent'

const AUTOSAVE_DELAY = 800
const TEXT_FIELDS = ['title', 'venue', 'websiteUrl', 'termsUrl', 'ticketImageUrl', 'descriptionImageUrl', 'description']
// Typed links — held back from autosave until they look like a full URL, so a
// half-typed link never blocks the rest of the form from saving.
const LINK_FIELDS = { websiteUrl: 'Website', termsUrl: 'Terms & Conditions' }
const isFullUrl = (value) => !value || /^https?:\/\/[^\s/.]+\.[^\s]+$/i.test(value)

/**
 * Basics step. Autosaves: every edit is debounced and PATCHes only the fields
 * whose API value changed since the last successful save. Emits
 * `status` ('saving' | 'saved' | 'error', message) for the footer and `saved`
 * with the fresh event after each save.
 */
export default {
    props: {
        event: { type: Object, required: true },
    },
    data() {
        return {
            form: this.formFromEvent(this.event),
            timezones: timezoneOptions(),
            lastSaved: null,
            timer: null,
            inFlight: null,
        }
    },
    created() {
        this.lastSaved = this.serverPayload(this.event)
        // A brand-new draft has no zone yet — the form defaults it to the browser's;
        // save that straight away so the dates mean something.
        if (Object.keys(this.changedFields()).length) this.queueSave()
    },
    beforeDestroy() {
        this.flush()
    },
    computed: {
        // Mirrors the API's basicsComplete() so the organiser can see why Basics isn't ticked.
        missing() {
            const f = this.form
            const checks = [
                [f.title, 'Event Name'],
                [f.eventDates && f.eventDates[0], 'Event Date(s)'],
                [f.timezone, 'Timezone'],
                [f.venue, 'Event Location'],
                [f.ticketImageUrl, 'Event Banner'],
                [f.description, 'Event Description'],
            ]
            return checks.filter(([value]) => !(typeof value === 'string' ? value.trim() : value)).map(([, label]) => label)
        },
    },
    watch: {
        form: {
            deep: true,
            handler() {
                this.queueSave()
            },
        },
    },
    methods: {
        formFromEvent(event) {
            const tz = event.timezone || browserTimezone()
            const range = (a, b) => (a && b ? [utcToWallClock(a, tz), utcToWallClock(b, tz)] : a ? [utcToWallClock(a, tz), utcToWallClock(a, tz)] : null)
            const form = { timezone: tz, eventDates: range(event.eventDate, event.eventEndDate), saleDates: range(event.saleStart, event.saleEnd) }
            TEXT_FIELDS.forEach((key) => (form[key] = event[key] || ''))
            if (form.title === 'Untitled event') form.title = ''
            return form
        },
        // What the API currently holds, in the same shape as toPayload().
        serverPayload(event) {
            const iso = (value) => (value ? new Date(value).toISOString() : null)
            const payload = {
                timezone: event.timezone || null,
                eventDate: iso(event.eventDate),
                eventEndDate: iso(event.eventEndDate),
                saleStart: iso(event.saleStart),
                saleEnd: iso(event.saleEnd),
            }
            TEXT_FIELDS.forEach((key) => (payload[key] = event[key] || null))
            if (payload.title === 'Untitled event') payload.title = null
            return payload
        },
        toPayload(form) {
            const tz = form.timezone
            const iso = (wall) => (wall ? wallClockToUtc(wall, tz).toISOString() : null)
            const [start, end] = form.eventDates || []
            const payload = {
                timezone: tz,
                eventDate: iso(start),
                // A zero-length range is how the picker shows "no end date".
                eventEndDate: end && start && end.getTime() !== start.getTime() ? iso(end) : null,
                saleStart: iso(form.saleDates && form.saleDates[0]),
                saleEnd: iso(form.saleDates && form.saleDates[1]),
            }
            TEXT_FIELDS.forEach((key) => (payload[key] = (form[key] || '').trim() || null))
            return payload
        },
        changedFields() {
            const next = this.toPayload(this.form)
            const diff = {}
            Object.keys(next).forEach((key) => {
                if (next[key] !== this.lastSaved[key] && this.editable(key) && isFullUrl(LINK_FIELDS[key] ? next[key] : null)) diff[key] = next[key]
            })
            return diff
        },
        invalidLinkMessage() {
            const bad = Object.keys(LINK_FIELDS).find((key) => !isFullUrl((this.form[key] || '').trim()))
            return bad ? `${this.langJson(LINK_FIELDS[bad])}: ${this.langJson('enter a full link, e.g. https://example.com')}` : ''
        },
        queueSave() {
            clearTimeout(this.timer)
            this.timer = setTimeout(() => this.save(), AUTOSAVE_DELAY)
        },
        flush() {
            clearTimeout(this.timer)
            return Object.keys(this.changedFields()).length ? this.save() : Promise.resolve()
        },
        async save() {
            // Serialise saves so an older response can't overwrite a newer one.
            if (this.inFlight) await this.inFlight.catch(() => {})
            const diff = this.changedFields()
            const linkError = this.invalidLinkMessage()
            if (!Object.keys(diff).length) {
                if (linkError) this.$emit('status', 'error', linkError)
                return
            }
            this.$emit('status', 'saving')
            this.inFlight = organizerApi.updateEvent(this.event.id, diff)
            try {
                const res = await this.inFlight
                Object.assign(this.lastSaved, diff)
                this.$emit('saved', res.data)
                if (linkError) this.$emit('status', 'error', linkError)
                else this.$emit('status', 'saved')
            } catch (err) {
                this.$emit('status', 'error', apiErrorMessage(err, this.langJson('Changes not saved')))
            } finally {
                this.inFlight = null
            }
        },
        // Dates and the sale window are guarded by their start field in the API's policy.
        editable(field) {
            const allowed = this.event.editableFields || []
            const key = { eventEndDate: 'eventDate', saleEnd: 'saleStart' }[field] || field
            return allowed.includes(key)
        },
        locked(field) {
            return !this.editable(field)
        },
        fieldClass(field) {
            return this.locked(field) ? 'event-basics--locked' : ''
        },
    },
}
</script>
<style lang="scss" scoped>
.event-basics {
    display: flex;
    flex-direction: column;
    gap: 32px;

    &__card {
        background: var(--color_white);
        border-radius: 32px;
        padding: 32px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
    &__card-title {
        font-family: 'Inter';
        font-weight: 700;
        font-size: 18px;
        color: var(--font_deep_color);
    }
    &__row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
    }
    &__field {
        min-width: 0;
    }
    &__label {
        font-family: 'Lato';
        font-size: 14px;
        line-height: 20px;
        color: var(--font_color_black);
        margin-bottom: 8px;
    }
    &__missing {
        padding: 16px 24px;
        border-radius: 24px;
        background: #fff6e5;
        color: #b54708;
        font-family: 'Lato';
        font-size: 14px;
    }
    &__req {
        color: var(--danger_color);
    }
    &__optional {
        color: var(--font_modif_color);
        font-size: 12px;
    }
    &__picker {
        width: 100% !important;

        ::v-deep .el-input__inner,
        &.el-range-editor {
            border-radius: 50px;
            border: none;
            background: var(--bg_color);
            height: 48px;
        }
        ::v-deep .el-range-input {
            background: transparent;
        }
    }
    &__desc {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
    }

    &--locked {
        opacity: 0.55;
        pointer-events: none;
    }
}

@media only screen and (max-width: 1080px) {
    .event-basics {
        gap: 16px;

        &__card {
            padding: 20px 16px;
            border-radius: 24px;
        }
        &__row,
        &__desc {
            grid-template-columns: 1fr;
        }
    }
}
</style>
